import { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    ButtonBase,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";

import { UUID } from "crypto";
import { RootState } from "@store/store";
import { DialogProps, useDialogs } from "@toolpad/core/useDialogs";

import { FinancialCompany, Product } from "@interfaces/catalogs";
import { DialogoBaseProps } from "@interfaces/components";
import { useLazyGetCompaniesQuery } from "@services/api/companies";
import { useLazyGetProductsQuery } from "@services/api/catalogs";
import { setFilterListByCompanies, setFilterListByFinancials } from "@store/slices/sessionSlice";
// import { CustomMultipleSelect } from "@components/CustomMultipleSelect";
import { Notificacion } from "./Notificacion";

/**
 * Tokens del diseño "Cambiar vista de empresa" (Claude Design · user-menu.js / lendia.css).
 * Se definen locales porque el diálogo se abre tanto bajo el tema v1 (`CustomHeader`)
 * como bajo `bpmV2Theme` (`TopbarProfile`).
 */
const ui = {
    navy800: "#002652",
    navy700: "#132B52",
    navy600: "#1A3D6B",
    navy300: "#A3D4E8",
    navy100: "#E3F2F8",
    green600: "#0D9373",
    textSecondary: "#7E90A4",
    textTertiary: "#7E90A4",
    border: "#D5E3EC",
    slate50: "#F9FAFB",
    fontDisplay: "'Outfit', system-ui, sans-serif",
};

const staticUrl = (path?: string | null) => (path ? `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${path}` : null);

interface CompanyOption {
    id: UUID;
    name: string;
    display_name: string;
    description: string;
    logoUrl: string | null;
}

interface TarjetaEmpresaProps {
    nombre: string;
    logoUrl: string | null;
    activa: boolean;
    seleccionada: boolean;
    descripcion?: string;
    productos?: Array<string>;
    onClick: () => void;
}

const TarjetaEmpresa = ({
    nombre,
    logoUrl,
    activa,
    seleccionada,
    descripcion,
    productos,
    onClick,
}: TarjetaEmpresaProps) => {
    const [logoFailed, setLogoFailed] = useState(false);

    return (
        <ButtonBase
            role="checkbox"
            aria-checked={seleccionada}
            onClick={onClick}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                justifyContent: "flex-start",
                gap: "7px",
                textAlign: "left",
                padding: "12px 13px",
                border: `1px solid ${seleccionada ? ui.navy600 : ui.border}`,
                borderRadius: "12px",
                backgroundColor: "#fff",
                boxShadow: seleccionada ? `0 0 0 2px ${ui.navy100}` : "none",
                transition: "border-color .12s, box-shadow .12s",
                "&:hover": { borderColor: seleccionada ? ui.navy600 : ui.navy300 },
                "&.Mui-focusVisible": { boxShadow: `0 0 0 3px ${ui.navy100}` },
            }}
        >
            <Box sx={{ height: 30, display: "flex", alignItems: "center" }}>
                {logoUrl && !logoFailed ? (
                    <Box
                        component="img"
                        src={logoUrl}
                        alt={nombre}
                        onError={() => setLogoFailed(true)}
                        sx={{ maxHeight: 30, maxWidth: 130, objectFit: "contain" }}
                    />
                ) : (
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: ui.fontDisplay,
                            fontSize: 18,
                            fontWeight: 700,
                            letterSpacing: "-.4px",
                            color: ui.navy800,
                            lineHeight: 1,
                        }}
                    >
                        {nombre}
                    </Typography>
                )}
            </Box>
            <Stack direction="row" sx={{ alignItems: "center", gap: "7px" }}>
                <Typography
                    component="span"
                    sx={{ fontFamily: ui.fontDisplay, fontSize: 13, fontWeight: 600, color: ui.navy800 }}
                >
                    {nombre}
                </Typography>
                {activa && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: ".05em",
                            textTransform: "uppercase",
                            color: ui.green600,
                            backgroundColor: "rgba(1,163,136,.12)",
                            padding: "2px 6px",
                            borderRadius: "99px",
                        }}
                    >
                        Activa
                    </Typography>
                )}
            </Stack>
            {descripcion && (
                <Typography component="span" sx={{ fontSize: 11, lineHeight: 1.4, color: ui.textSecondary }}>
                    {descripcion}
                </Typography>
            )}
            {productos && productos.length > 0 && (
                <Typography component="span" sx={{ fontSize: 10.5, color: ui.textTertiary }}>
                    {`${productos.length} producto${productos.length === 1 ? "" : "s"} · ${productos.join(", ")}`}
                </Typography>
            )}
        </ButtonBase>
    );
};

const EncabezadoSeccion = ({
    titulo,
    todos,
    onToggleTodos,
}: {
    titulo: string;
    todos: boolean;
    onToggleTodos: () => void;
}) => (
    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: "10px" }}>
        <Typography
            component="span"
            sx={{
                fontFamily: ui.fontDisplay,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: ui.textTertiary,
            }}
        >
            {titulo}
        </Typography>
        <Button
            size="small"
            onClick={onToggleTodos}
            sx={{ fontFamily: ui.fontDisplay, fontSize: 12, fontWeight: 600, color: ui.navy600, textTransform: "none" }}
        >
            {todos ? "Deseleccionar todos" : "Seleccionar todos"}
        </Button>
    </Stack>
);

const gridTarjetas = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    gap: "10px",
};

export const CambiarVistaCompania = (props: DialogProps<DialogoBaseProps>) => {
    const dialogs = useDialogs();
    const dispatch = useDispatch();
    const hasFetched = useRef(false);

    const [financialCoSelected, setFinancialCoSelected] = useState<
        Array<Pick<FinancialCompany, "id" | "name" | "code">>
    >([]);

    // const [allCompaniesChecked, setAllCompaniesChecked] = useState<boolean>(true);
    // const [allFinancialsChecked, setAllFinancialsChecked] = useState<boolean>(true);

    const [values, setValues] = useState<Array<UUID>>([]);
    const [companies, setCompanies] = useState<Array<CompanyOption>>([]);
    const [productsByCompany, setProductsByCompany] = useState<Record<string, Array<Product>>>({});

    const companiesUserAssigned = useSelector((state: RootState) => state.session.companies);
    const companiesFilters = useSelector((state: RootState) => state.session.filters.shared_filters.companies);

    const financialUserAssigned = useSelector((state: RootState) => state.session.financial_inc);
    const financialsFilters = useSelector((state: RootState) => state.session.filters.shared_filters.financials);

    const [triggerGetCompanies] = useLazyGetCompaniesQuery();
    const [triggerGetProducts] = useLazyGetProductsQuery();

    const allCompaniesChecked = useMemo(() => {
        return values.length === companies.length;
    }, [values]);

    const allFinancialsChecked = useMemo(() => {
        return financialCoSelected.length === financialUserAssigned.length;
    }, [financialCoSelected]);

    const vistaActual = useMemo(() => {
        if (companiesFilters.length === 0) return "—";
        if (companies.length > 1 && companiesFilters.length === companies.length) return "Todas";
        return companiesFilters.map((c) => c.display_name ?? c.name).join(", ");
    }, [companiesFilters, companies]);

    const handleToggleCompany = async (companyId: UUID, checked: boolean) => {
        const staging = checked ? values.concat([companyId]) : values.filter((o) => o !== companyId);
        setValues(staging);

        if (companies.find((c) => c.id === companyId)?.name === "PYMIO") {
            const query = checked
                ? financialCoSelected.length !== financialUserAssigned.length
                : financialCoSelected.length !== 0;
            if (query) {
                const response = await dialogs.open(Notificacion, {
                    type: "CONFIRM",
                    noText: "No",
                    text: `¿Quiere que también se ${checked ? "" : "des"}marquen todas las financieras?`,
                });

                if (response === false) return;

                setFinancialCoSelected(checked ? financialUserAssigned : []);
            }
        }

        let financialRelated = financialUserAssigned.filter((f) => f.company_id === companyId);

        financialRelated = checked
            ? financialRelated.filter((t) => financialCoSelected.find((c) => c.id === t.id) === undefined)
            : financialRelated.filter((t) => financialCoSelected.find((c) => c.id === t.id));

        if (financialRelated.length !== 0) {
            const response = await dialogs.open(Notificacion, {
                type: "CONFIRM",
                noText: "No",
                text: `La compañia seleccionada tiene asociada una financiera, ¿También quiere que sea ${checked ? "seleccionada" : "deseleccionada"}?`,
            });
            if (response === false) return;

            setFinancialCoSelected((oldValues) =>
                checked
                    ? oldValues.concat(financialRelated)
                    : oldValues.filter((o) => financialRelated.map((f) => f.id).includes(o.id) === false),
            );
        }
    };

    const handleToggleAllCompanies = () => {
        const checked = !allCompaniesChecked;
        setValues(checked ? companies.map((c) => c.id) : []);

        if (companies.find((c) => c.name === "PYMIO")) {
            setFinancialCoSelected(checked ? financialUserAssigned : []);
        }
    };

    const handleToggleFinancial = (financialCo: FinancialCompany, checked: boolean) => {
        setFinancialCoSelected((oldValues) =>
            checked ? oldValues.concat([financialCo]) : oldValues.filter((f) => f.id !== financialCo.id),
        );
    };

    const handleOnViewChangeClicked = async () => {
        let selectedCompanies = [];
        if (values.includes("0-0-0-0-0")) {
            selectedCompanies = companies;
        } else {
            selectedCompanies = companies.filter((company) => values.includes(company.id) === true);
        }

        dispatch(
            setFilterListByCompanies(
                selectedCompanies.map(({ id, name, display_name }) => ({ id, name, display_name })),
            ),
        );
        dispatch(setFilterListByFinancials(financialCoSelected === undefined ? [] : financialCoSelected));
        // if (selectedCompanies.find((t) => t.name === "PYMIO")) {
        //     dispatch(setFilterListByFinancials(financialCoSelected === undefined ? [] : financialCoSelected));
        // } else {
        //     dispatch(setFilterListByFinancials([]));
        // }
        props.onClose();
    };

    const loadProducts = async (companyIds: Array<UUID>) => {
        const results = await Promise.allSettled(
            companyIds.map((id) =>
                triggerGetProducts(new URLSearchParams([["q", `company_id:${id}`]]).toString()).unwrap(),
            ),
        );

        const byCompany: Record<string, Array<Product>> = {};
        results.forEach((result, idx) => {
            if (result.status === "fulfilled") byCompany[companyIds[idx]] = result.value;
        });
        setProductsByCompany(byCompany);
    };

    const loadInfo = async () => {
        let options: Array<CompanyOption>;

        if (companiesUserAssigned.length === 0) {
            const companies = await triggerGetCompanies().unwrap();
            options = companies.map((company) => ({
                id: company.id,
                name: company.display_name,
                display_name: company.display_name,
                description: company.description,
                logoUrl: staticUrl(company.palette?.interface?.navbar?.logo),
            }));
        } else {
            options = companiesUserAssigned
                .map((company) => ({
                    id: company.id,
                    name: company.name,
                    display_name: company.display_name,
                    description: company.description,
                    logoUrl: staticUrl(company.palette?.interface?.navbar?.logo),
                }))
                .sort((a, b) => a.display_name.localeCompare(b.display_name));

            // La sesión puede no traer la paleta completa: se completan logos/descripciones desde el catálogo.
            if (options.some((o) => o.logoUrl === null || !o.description)) {
                try {
                    const catalog = await triggerGetCompanies().unwrap();
                    options = options.map((o) => {
                        const full = catalog.find((c) => c.id === o.id);
                        return {
                            ...o,
                            description: o.description || full?.description || "",
                            logoUrl: o.logoUrl ?? staticUrl(full?.palette?.interface?.navbar?.logo),
                        };
                    });
                } catch {}
            }
        }

        setCompanies(options);
        loadProducts(options.map((o) => o.id));

        setValues(companiesFilters.map((t) => t.id));

        if (financialsFilters) {
            setFinancialCoSelected(financialsFilters);
        }
    };

    useEffect(() => {
        if (hasFetched.current) return;

        loadInfo();

        hasFetched.current = true;
    }, [hasFetched]);

    return (
        <Dialog
            fullWidth={props.payload.fullWidth}
            maxWidth={props.payload.maxWidth}
            open={props.open}
            onClose={() => props.onClose()}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "0 30px 80px rgba(0,0,0,.25)",
                        overflow: "hidden",
                    },
                },
            }}
        >
            <DialogTitle
                component="div"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "18px 22px 14px",
                    borderBottom: `1px solid ${ui.border}`,
                }}
            >
                <Typography
                    component="h3"
                    sx={{ m: 0, fontFamily: ui.fontDisplay, fontSize: 17, fontWeight: 600, color: ui.navy800 }}
                >
                    Cambiar vista de empresa
                </Typography>
                <IconButton
                    aria-label="Cerrar"
                    onClick={() => props.onClose()}
                    sx={{ ml: "auto", width: 30, height: 30, borderRadius: "8px", color: ui.textTertiary }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "16px 22px !important" }}>
                <EncabezadoSeccion
                    titulo="Compañías"
                    todos={allCompaniesChecked}
                    onToggleTodos={handleToggleAllCompanies}
                />
                <Box sx={gridTarjetas}>
                    {companies.map((company) => {
                        const seleccionada = values.includes(company.id);
                        return (
                            <TarjetaEmpresa
                                key={company.id}
                                nombre={company.display_name}
                                logoUrl={company.logoUrl}
                                descripcion={company.description}
                                productos={productsByCompany[company.id]?.map((p) => p.name)}
                                activa={companiesFilters.some((c) => c.id === company.id)}
                                seleccionada={seleccionada}
                                onClick={() => handleToggleCompany(company.id, !seleccionada)}
                            />
                        );
                    })}
                </Box>
                <Collapse
                    in={
                        values.includes(companies.find((t) => t.name === "PYMIO")?.id ?? ("" as UUID)) ||
                        financialCoSelected.length !== 0
                    }
                >
                    <Box sx={{ mt: "20px" }}>
                        <EncabezadoSeccion
                            titulo="Financieras"
                            todos={allFinancialsChecked || financialUserAssigned.length === financialCoSelected.length}
                            onToggleTodos={() =>
                                // setAllFinancialsChecked(checked);
                                setFinancialCoSelected(
                                    allFinancialsChecked || financialUserAssigned.length === financialCoSelected.length
                                        ? []
                                        : financialUserAssigned,
                                )
                            }
                        />
                        <Box sx={gridTarjetas}>
                            {[...financialUserAssigned]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((financialCo) => {
                                    const seleccionada = financialCoSelected.some((f) => f.id === financialCo.id);
                                    return (
                                        <TarjetaEmpresa
                                            key={financialCo.id}
                                            nombre={financialCo.name}
                                            logoUrl={staticUrl(financialCo.logo)}
                                            descripcion={financialCo.description}
                                            activa={(financialsFilters ?? []).some((f) => f.id === financialCo.id)}
                                            seleccionada={seleccionada}
                                            onClick={() => handleToggleFinancial(financialCo, !seleccionada)}
                                        />
                                    );
                                })}
                        </Box>
                    </Box>
                </Collapse>
            </DialogContent>
            <DialogActions
                sx={{
                    gap: "10px",
                    padding: "12px 22px",
                    borderTop: `1px solid ${ui.border}`,
                    backgroundColor: ui.slate50,
                    "& > :not(style) ~ :not(style)": { ml: 0 },
                }}
            >
                <Typography sx={{ mr: "auto", fontSize: 11.5, color: ui.textSecondary }}>
                    Vista actual:{" "}
                    <Box component="b" sx={{ color: ui.navy600, fontWeight: 600 }}>
                        {vistaActual}
                    </Box>
                </Typography>
                <Button
                    onClick={() => props.onClose()}
                    sx={{
                        height: 34,
                        px: "14px",
                        borderRadius: "8px",
                        border: `1px solid ${ui.border}`,
                        backgroundColor: "#fff",
                        fontFamily: ui.fontDisplay,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: ui.navy800,
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: ui.navy100 },
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    disabled={
                        values.length === 0 ||
                        (values.includes(companies.find((t) => t.name === "PYMIO")?.id ?? ("" as UUID)) &&
                            financialCoSelected?.length === 0)
                    }
                    onClick={handleOnViewChangeClicked}
                    sx={{
                        height: 34,
                        px: "14px",
                        borderRadius: "8px",
                        border: `1px solid ${ui.navy800}`,
                        backgroundColor: ui.navy800,
                        fontFamily: ui.fontDisplay,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "#fff",
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: ui.navy700 },
                        "&.Mui-disabled": { color: "#fff", opacity: 0.5 },
                    }}
                >
                    Cambiar vista
                </Button>
            </DialogActions>
        </Dialog>
    );
};
